/* SIZESOFTX
=========================================================================================
* Descripción: Context del catálogo de Corridas (tallas)
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1        | 25/04/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 23/09/2026 | LIC. José Luis Aragón C.                       | Ortografía: caracteres reparados a UTF-8
* ---------------------------------------------------------------------------------------
*/

import { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { isErrorForm, handleValidarStrings, getDatePost } from '../../settings/utils';
import { defaultTypes } from "../../settings/enums";
import {
    apiListaCorridas,
    apiCorridasSave,
    apiCorridasDelete,
    apiCorridasDeleteAll,
} from "../../settings/ApiConfig";
import { useRequest } from "../System/RequestContext";
import { useLayout } from "../System/LayoutContext";

const getStructCorridas = () => ({
    descripcion: "",
    medidainicial: 0,
    medidafinal: 0,
    intervalos: 0,
    fecharegistro: getDatePost(),
});

const getStructCorridasPayload = () => ({
    descripcion: { value: "", error: false, helper: "" },
    medidainicial: { value: 0, error: false, helper: "" },
    medidafinal: { value: 0, error: false, helper: "" },
    intervalos: { value: 0, error: false, helper: "" },
});

export const CorridasContext = createContext();
export const CorridasProvider = (props) => {
    const { GetRequest, PostRequest } = useRequest();
    const { handleOpenAlert } = useLayout();
    const navigation = useNavigate();

    const [corridaSeleccionada, setCorridaSeleccionada] = useState(getStructCorridas());
    const [corridaPayLoad, setCorridaPayLoad] = useState(getStructCorridasPayload());
    const [listaCorridas, setListaCorridas] = useState([]);
    const [statusEditCorridas, setStatusEditCorridas] = useState(true);
    const [statusEnableFields, setStatusEnableFields] = useState(true);
    const [statusOpenModalEliminarCorrida, setStatusOpenModalEliminarCorrida] = useState(false);

    const handleClickRegresarMainDashboard = () => {
        navigation("/*");
    };

    const handleChangeDataCorridas = (event, newValue, fieldName) => {
        if (event?.target?.name) {
            const { name, value } = event.target;
            setCorridaSeleccionada(prev => ({ ...prev, [name]: value }));
            return;
        }
        if (newValue !== null && fieldName) {
            const value = typeof newValue === "string" ? newValue : newValue?.[fieldName] || "";
            setCorridaSeleccionada(prev => ({ ...prev, [fieldName]: value }));
        }
    };

    const handleChangeDataCorridasPayload = (e, valor, field) => {
        let name = field ? field : e.target.name ?? "";
        let value = valor !== undefined ? valor : e.target.value ?? "";
        let error = false;
        let helper = "";

        // Validación para campos de texto (descripcion)
        if (name === "descripcion") {
            const mensajeError = handleValidarStrings(value, name);
            if (mensajeError) {
                error = true;
                helper = mensajeError;
            }

            const lower = String(value).toLowerCase();
            if (!error && (
                lower.includes("<script") ||
                lower.includes("</script") ||
                lower.includes("javascript:") ||
                lower.includes("onerror=") ||
                lower.includes("onload=") ||
                lower.includes("<img") ||
                lower.includes("<svg") ||
                lower.includes("onload") ||
                lower.includes("onerror") ||
                lower.includes("data:") ||
                lower.includes("vbscript:")
            )) {
                error = true;
                helper = "Intento de inyección detectado y bloqueado en: (" + name + ")";
            }

            // Validar longitud máxima
            if (!error && value.length > 30) {
                error = true;
                helper = "La descripción no puede exceder 30 caracteres";
            }

            // Validar caracteres peligrosos para SQL
            if (!error) {
                const sqlDangerous = /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|ALTER|CREATE|TRUNCATE)\b)/i;
                if (sqlDangerous.test(value)) {
                    error = true;
                    helper = "Caracteres no permitidos detectados";
                }
            }
        }

        // Validación para campos numéricos
        if (name === "medidainicial" || name === "medidafinal" || name === "intervalos") {
            const numValue = parseFloat(value);
            
            if (isNaN(numValue)) {
                error = true;
                helper = "Debe ser un número válido";
            } else if (numValue < 0) {
                error = true;
                helper = "El valor no puede ser negativo";
            } else if (numValue > 9999) {
                error = true;
                helper = "El valor excede el límite permitido (9999)";
            }
            
            // Para decimals, validar hasta 4 decimales
            if (!error && value.toString().includes('.')) {
                const decimals = value.toString().split('.')[1];
                if (decimals && decimals.length > 4) {
                    error = true;
                    helper = "Máximo 4 decimales permitidos";
                }
            }
        }

        setCorridaPayLoad(prev => {
            const prevValue = prev[name]?.value ?? "";
            const newValueStr = String(value ?? "");
            const prevValueStr = String(prevValue ?? "");
            if (prevValueStr === newValueStr) return prev;
            return {
                ...prev,
                [name]: { ...prev[name], value, error, helper },
            };
        });
    };

    const handleObtenerCorridasList = async () => {
        const result = await GetRequest({
            url: apiListaCorridas,
            loader: "Cargando lista de Corridas...",
        });

        if (result.hasError) {
            handleOpenAlert("Error al obtener la lista de Corridas: " + result.message);
            return;
        }
        setListaCorridas(result?.result ?? []);
    };

    const handleClickNuevaCorrida = () => {
        handleClickDeshacer();
        setStatusEditCorridas(false);
        setStatusEnableFields(false);
    };

    const handleClickEditarCorrida = () => {
        setStatusEditCorridas(false);
        setStatusEnableFields(false);
    };

    const handleClickDeshacer = () => {
        setCorridaSeleccionada(getStructCorridas());
        setCorridaPayLoad(getStructCorridasPayload());
        setStatusEditCorridas(true);
        setStatusEnableFields(true);
    };

    const handleClickGuardarCorrida = async () => {
        if (
            corridaPayLoad.descripcion.value.trim().length === 0
        ) {
            handleOpenAlert("Es necesario especificar la descripción de la Corrida para continuar!");
            return;
        }

        const esModoNuevo = !corridaSeleccionada?.descripcion || corridaSeleccionada.descripcion.trim() === "";
        
        if (!esModoNuevo && corridaPayLoad.descripcion.value.trim().toUpperCase() !== corridaSeleccionada.descripcion.trim().toUpperCase()) {
            handleOpenAlert("No es posible modificar la descripción (clave) de una Corrida existente. Cree una nueva Corrida si requiere valores diferentes.");
            return;
        }

        if (isErrorForm(corridaPayLoad)) {
            handleOpenAlert("Existen errores en los datos de la Corrida y no es posible continuar!");
            return;
        }

        // Validar lógica de medidas
        const medidaInicial = parseFloat(corridaPayLoad.medidainicial.value) || 0;
        const medidaFinal = parseFloat(corridaPayLoad.medidafinal.value) || 0;
        const intervalos = parseFloat(corridaPayLoad.intervalos.value) || 0;

        if (medidaInicial > medidaFinal) {
            handleOpenAlert("La medida inicial debe ser menor que la medida final!");
            return;
        }

        if (intervalos <= 0) {
            handleOpenAlert("El intervalo debe ser mayor a cero!");
            return;
        }

        const resultPayload = {};
        const sanitizeString = (value) => {
            if (typeof value !== "string") return value;
            // Eliminar caracteres de control y espacios
            return value
                .replace(/[\x00-\x1F\x7F]/g, "") // Caracteres de control
                .replace(/[\u200B-\u200D\uFEFF]/g, "") // Caracteres invisibles
                .trim();
        };

        const upperIfString = (v, key) => {
            if (typeof v !== "string") return v;
            return sanitizeString(v).toUpperCase();
        };

        Object.entries(corridaPayLoad).forEach(([key, meta]) => {
            if (meta && typeof meta === "object" && "value" in meta) {
                let value = meta.value;
                // Para campos numéricos, asegurar que sean números válidos
                if (key === "medidainicial" || key === "medidafinal" || key === "intervalos") {
                    value = parseFloat(value) || 0;
                }
                resultPayload[key] = upperIfString(value, key);
            } else {
                resultPayload[key] = upperIfString(meta, key);
            }
        });

        const result = await PostRequest({
            url: apiCorridasSave,
            body: resultPayload,
            loader: "Guardando datos de la Corrida...",
        });

        if (result.hasError || result.errors) {
            handleOpenAlert("Error al guardar datos de la Corrida: " + result.message);
            return;
        }

        handleOpenAlert("Corrida guardada correctamente", "ok");
        setStatusEditCorridas(true);
        setStatusEnableFields(true);
        
        if (result.result && result.result.descripcion) {
            setCorridaSeleccionada(result.result);
            const payload = getStructCorridasPayload();
            Object.keys(payload).forEach((key) => {
                if (result.result[key] !== undefined) {
                    payload[key] = { value: result.result[key] ?? "", error: false, helper: "" };
                }
            });
            setCorridaPayLoad(payload);
        } else {
            setCorridaSeleccionado(resultPayload);
        }
        
        await handleObtenerCorridasList();
    };

const handleClickEliminarCorrida = async () => {
        if (!corridaSeleccionada.descripcion || corridaSeleccionada.descripcion.trim().length === 0) return;

        const result = await PostRequest({
            url: apiCorridasDelete.replace("{descripcion}", encodeURIComponent(corridaSeleccionada.descripcion.trim())),
            body: {},
            loader: "Eliminando Corrida...",
        });
 
        if (result.hasError || result.errors) {
            handleOpenAlert("Error al eliminar la Corrida: " + result.message);
            return;
        }
 
        setStatusOpenModalEliminarCorrida(false);
        handleOpenAlert("Corrida eliminada correctamente", "ok");
        await handleObtenerCorridasList();
        handleClickDeshacer();
    };

    const handleClickRefrescarDatos = async () => {
        await handleObtenerCorridasList();
        handleOpenAlert("Datos actualizados correctamente", "ok");
    };

    const handleClickEliminarTodasCorridas = async () => {
        const result = await PostRequest({
            url: apiCorridasDeleteAll,
            body: {},
            loader: "Eliminando todas las Corridas...",
        });

        if (result.hasError || result.errors) {
            handleOpenAlert("Error al eliminar todas las Corridas: " + result.message);
            return;
        }

        handleOpenAlert(result.result?.mensaje || "Todas las Corridas eliminadas correctamente", "ok");
        setListaCorridas([]);
        handleClickDeshacer();
    };

    return (
        <CorridasContext.Provider
            value={{
                corridaSeleccionada, setCorridaSeleccionada,
                corridaPayLoad, setCorridaPayLoad, getStructCorridasPayload,
                listaCorridas,
                statusEditCorridas, setStatusEditCorridas,
                statusEnableFields, setStatusEnableFields,
                statusOpenModalEliminarCorrida, setStatusOpenModalEliminarCorrida,
                handleClickRegresarMainDashboard,
                handleChangeDataCorridas,
                handleChangeDataCorridasPayload,
                handleObtenerCorridasList,
                handleClickNuevaCorrida,
                handleClickEditarCorrida,
                handleClickDeshacer,
                handleClickGuardarCorrida,
                handleClickEliminarCorrida,
                handleClickRefrescarDatos,
                handleClickEliminarTodasCorridas,
            }}
        >
            {props.children}
        </CorridasContext.Provider>
    );
};

export const useCorridas = () => useContext(CorridasContext);